import { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import type { DippyBubble } from '@/stores/dippy-store';

interface DippySpeechBubbleProps {
  bubble: DippyBubble | null;
  onDismiss: () => void;
  onClick: () => void;
}

export default function DippySpeechBubble({ bubble, onDismiss, onClick }: DippySpeechBubbleProps) {
  useEffect(() => {
    if (!bubble) return;
    const ms = bubble.autoMs ?? 8000;
    const timer = setTimeout(onDismiss, ms);
    return () => clearTimeout(timer);
  }, [bubble?.id]);

  return (
    <AnimatePresence>
      {bubble && (
        <motion.div
          key={bubble.id}
          initial={{ opacity: 0, scale: 0.85, y: 8 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.85, y: 8 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className="absolute bottom-20 left-0 w-64 bg-white rounded-xl shadow-lg border border-stone-200 p-3 cursor-pointer"
          onClick={onClick}
          data-testid="dippy-speech-bubble"
        >
          {/* Close button */}
          <button
            onClick={(e) => { e.stopPropagation(); onDismiss(); }}
            className="absolute top-2 right-2 text-stone-400 hover:text-stone-600"
            aria-label="Dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </button>

          {/* Content */}
          <div className="pr-4">
            {bubble.type === 'alert' && (
              <span className="text-[10px] uppercase font-bold text-red-600 tracking-wider">Alert</span>
            )}
            {bubble.type === 'tip' && (
              <span className="text-[10px] uppercase font-bold text-blue-600 tracking-wider">Tip</span>
            )}
            <p className="text-sm text-stone-800 mt-0.5 leading-snug">{bubble.message}</p>
            {bubble.action && (
              <span className="text-xs text-green-700 font-medium mt-1 inline-block">
                {bubble.action.label} &rarr;
              </span>
            )}
          </div>

          {/* Tail pointer */}
          <div className="absolute -bottom-2 left-6 w-4 h-4 bg-white border-l border-b border-stone-200 transform rotate-45" />
        </motion.div>
      )}
    </AnimatePresence>
  );
}
